import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CommonAttribute,
  CommonAttributeRequest,
  CommonAttributesResponse,
  CommonAttributeResponse,
  DeleteResponse,
  KpiOperator,
  KpiOperatorsResponse
} from '../models/kpi.model';

/**
 * Interface for widget request payload
 */
export interface WidgetRequest {
  uidCustomer: string;
  signals?: string[];
  uidsKpi?: string[];
  fromDate: string; // ISO date-time format
  toDate: string; // ISO date-time format
  timezone?: string;
  aggregate: boolean;
  uidVehicles?: string[];
}

/**
 * Interface for generic API response
 */
export interface ObjectApiResponse {
  result: string; // 'OK' or 'ERROR'
  errorDescription: string | null;
  dataResponseSizeInKb?: string;
  elapsedTime: string;
  data: any;
  mappings?: any;
  isFromCache: boolean;
}

/**
 * Service for managing Common Attributes (KPI) CRUD operations
 */
@Injectable({
  providedIn: 'root'
})
export class KpiCrudService {
  private readonly baseUrl = `${environment.baseTsOhwUrl}v1/rt/common-attributes`;
  private readonly kpiOperatorsUrl = `${environment.baseTsOhwUrl}v1/rt/kpi-operators`;
  private readonly functionsUrl = `${environment.baseTsOhwUrl}v1/rt/functions/execute`;
  private readonly signalDataUrl = `${environment.baseTsOhwUrl}v1/rt/kpi/data`;

  private readonly compositeSignalDataUrl = `${environment.baseTsOhwUrl}v1/rt/kpi/custom`;

  constructor(private http: HttpClient) {}

  /**
   * Get all common attributes with optional scope filter
   * @param scope Optional scope filter
   * @returns Observable of API response with common attributes array
   */
  getAllCommonAttributes(scope?: string): Observable<CommonAttributesResponse> {
    let params = new HttpParams();
    if (scope) {
      params = params.set('scope', scope);
    }

    return this.http.get<CommonAttributesResponse>(`${this.baseUrl}/all`, { params });
  }

  /**
   * Get common attributes by UIDs
   * @param uids Array of UIDs to fetch
   * @returns Observable of API response with common attributes array
   */
  getCommonAttributesByUids(uids: string[]): Observable<CommonAttributesResponse> {
    let params = new HttpParams();
    uids.forEach(uid => {
      params = params.append('uids', uid);
    });

    return this.http.get<CommonAttributesResponse>(`${this.baseUrl}/uids`, { params });
  }

  /**
   * Get common attributes by customer UID
   * @param customerUid Customer UID
   * @param scope Optional scope filter
   * @returns Observable of API response with common attributes array
   */
  getCommonAttributesByCustomer(
    customerUid: string,
    scope?: string
  ): Observable<CommonAttributesResponse> {
    let params = new HttpParams();
    if (scope) {
      params = params.set('scope', scope);
    }

    return this.http.get<CommonAttributesResponse>(
      `${this.baseUrl}/customer/${customerUid}`,
      { params }
    );
  }

  /**
   * Create a new common attribute
   * @param request Common attribute request data
   * @returns Observable of API response with created common attribute
   */
  createCommonAttribute(request: CommonAttributeRequest): Observable<CommonAttributeResponse> {
    return this.http.post<CommonAttributeResponse>(this.baseUrl, request);
  }

  /**
   * Update an existing common attribute by UID
   * @param uid UID of the common attribute to update
   * @param request Updated common attribute data
   * @returns Observable of API response with updated common attribute
   */
  updateCommonAttribute(
    uid: string,
    request: CommonAttributeRequest
  ): Observable<CommonAttributeResponse> {
    return this.http.put<CommonAttributeResponse>(`${this.baseUrl}/${uid}`, request);
  }

  /**
   * Delete a common attribute by UID.
   * Il BE gestisce l'associazione ai customer tramite customerUids: va sempre
   * passato come array (quello letto in fase di load del catalogo).
   * @param uid UID of the common attribute to delete
   * @param customerUids Array di customer a cui l'attributo è associato
   * @returns Observable of API response
   */
  deleteCommonAttribute(uid: string, customerUids: string[] = []): Observable<DeleteResponse> {
    return this.http.delete<DeleteResponse>(`${this.baseUrl}/${uid}`, {
      body: { customerUids }
    });
  }

  /**
   * Get all KPI operators
   * @returns Observable of API response with KPI operators array
   */
  getAllKpiOperators(): Observable<KpiOperatorsResponse> {
    return this.http.get<KpiOperatorsResponse>(this.kpiOperatorsUrl);
  }

  /**
   * Execute a function via the functions API
   * @param functionName Name of the function to execute
   * @param params Parameters to pass to the function
   * @returns Observable of API response
   */
  executeFunction<T = any>(functionName: string, params: any[]): Observable<T> {
    const body = {
      function: functionName,
      params: params
    };

    return this.http.post<T>(this.functionsUrl, body);
  }

  /**
   * Get signal data (for scope = 'kpi_custom')
   * @param request Widget request with signals and parameters
   * @returns Observable of API response with signal data
   */
  getSignalData(request: WidgetRequest): Observable<ObjectApiResponse> {
    return this.http.post<ObjectApiResponse>(this.signalDataUrl, request);
  }

  /**
   * Get composite signal data (for scope = 'kpi')
   * @param request Widget request with signals and parameters
   * @returns Observable of API response with composite signal data
   */
  getCompositeSignalData(request: WidgetRequest): Observable<ObjectApiResponse> {
    return this.http.post<ObjectApiResponse>(this.compositeSignalDataUrl, request);
  }
}
